import {computed, inject} from '@angular/core';
import {signalStore, withState, withComputed, withMethods, patchState} from '@ngrx/signals';
import {take} from 'rxjs/operators';
import {LookupService} from '../services/lookup.service';
import {LookupValue} from '../../clm/types/lookup-value.enum';
import {NameValueCodeDTO} from '../models/NameValueCodeDTO';
import {PagedFilterDTONew} from '../models/PagedFilterDTO';

export interface ProductState {
  lookupValues: NameValueCodeDTO[];
  loaded: boolean;
  cachedSelectedProducts: string[] | null;
}

const initialState: ProductState = {
  lookupValues: [],
  loaded: false,
  cachedSelectedProducts: null
};

export const ProductStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withComputed((state) => {
    const productNames = computed(() => state.lookupValues().map(dto => dto.name));
    return {
      productNames,
      productDefaults: computed(() => ['Select All', ...productNames()]),
    };
  }),
  withMethods((store, lookupService = inject(LookupService)) => ({
    loadProducts(): void {
      if (store.loaded()) {
        return;
      }

      lookupService.getListOfLookupValuesWithCode(LookupValue.Product)
        .pipe(take(1))
        .subscribe({
          next: (response: NameValueCodeDTO[]) => {
            patchState(store, {
              lookupValues: response,
              loaded: true
            });
          },
          error: (err) => {
            console.error('Failed to load products:', err);
            patchState(store, {lookupValues: [], loaded: false});
          }
        });
    },

    getProductCodesBySelectedNames(selectedNames: string[]): string[] {
      return store.lookupValues()
        .filter(dto => selectedNames.includes(dto.name))
        .map(dto => dto.code);
    },

    isAllProductsSelected(selectedNames: string[]): boolean {
      return selectedNames?.length - 1 === store.lookupValues()?.length;
    },

    cacheSelectedProducts(products: string[]): void {
      patchState(store, {cachedSelectedProducts: products});
    },

    buildProductFilter(selectedProducts: string[], fieldName: string): PagedFilterDTONew | null {
      if (!selectedProducts?.length || this.isAllProductsSelected(selectedProducts)) {
        return null;
      }

      const codes = this.getProductCodesBySelectedNames(selectedProducts);
      if (!codes.length) {
        return null;
      }

      const filter = new PagedFilterDTONew();
      filter.name = fieldName;
      filter.operator = 'in';
      filter.value = codes.join(',');
      return filter;
    }
  }))
);
